import { IncomingHttpHeaders, IncomingMessage, ServerResponse } from "http";

import { GQLEnumResourceTypeEnum } from "@framework/__generated__/graphql/enums";
import { log } from "@voxmedia/duet-utils/logging";

export const parseHeaderValue = (
  headers: IncomingHttpHeaders,
  key: string,
  throwIfUndefined: boolean = false,
): string | null => {
  const value = headers[key];

  if (value === undefined) {
    if (throwIfUndefined) {
      throw new Error(`Header '${key}' is not defined.`);
    } else {
      return null;
    }
  }

  if (typeof value !== "string") {
    throw new Error(`Header '${key}' is not a string.`);
  }

  return value;
};

export const parseCommunityIdHeader = (
  headers: IncomingHttpHeaders,
): number => {
  const communityId = Number(
    parseHeaderValue(headers, "vm-community-id", true)!,
  );

  return Number(communityId);
};

/**
 * Safely parses the community ID header with a fallback value.
 * This is useful for cases where middleware might be skipped (e.g., crawlers hitting non-existent assets)
 * but a value is expected to be set for communityId. The fallback should typically be
 * the primary community ID for the network, obtained from getPrimaryCommunityId().
 *
 * @param headers - The request headers
 * @param fallback - Fallback community ID to use if header is missing (typically primary community ID)
 */
export const safeParseCommunityIdHeader = (
  headers: IncomingHttpHeaders,
  fallback: number,
): number => {
  const communityId = parseHeaderValue(
    headers,
    "vm-community-id",
    false, // Don't throw if undefined
  );

  if (!communityId) {
    log.warn(
      `Using fallback community ID (${fallback}) - likely due to middleware being skipped (e.g., crawlers hitting non-existent assets)`,
    );
    return Number(fallback);
  }

  return Number(communityId);
};

export const parseCycloneCommunityIdHeader = (
  headers: IncomingHttpHeaders,
): string => {
  const cycloneCommunityId = parseHeaderValue(
    headers,
    "vm-cyclone-community-id",
    true,
  )!;
  return cycloneCommunityId;
};

/**
 * Safely parses the cyclone community ID header with a fallback value.
 * This is useful for cases where middleware might be skipped (e.g., crawlers hitting non-existent assets)
 * but a value is expected to be set for cycloneCommunityId. The fallback should typically be
 * the primary cyclone community ID for the network, obtained from getPrimaryCycloneCommunityId().
 *
 * @param headers - The request headers
 * @param fallback - Fallback cyclone community ID to use if header is missing (typically primary cyclone community ID)
 */
export const safeParseCycloneCommunityIdHeader = (
  headers: IncomingHttpHeaders,
  fallback: string,
): string => {
  const cycloneCommunityId = parseHeaderValue(
    headers,
    "vm-cyclone-community-id",
    false, // Don't throw if undefined
  );

  if (!cycloneCommunityId) {
    log.warn(
      `Using fallback cyclone community ID (${fallback}) - likely due to middleware being skipped (e.g., crawlers hitting non-existent assets)`,
    );
    return fallback;
  }

  return cycloneCommunityId;
};

export const parseIsPaywallEligibleHeader = (
  headers: IncomingHttpHeaders,
): boolean => {
  const isPaywallEligible =
    parseHeaderValue(headers, "vm-resource-is-paywall-eligible") === "true";

  return isPaywallEligible;
};

export const parseContentResourceHeaders = (
  headers: IncomingHttpHeaders,
  res?: ServerResponse<IncomingMessage>,
) => {
  const communityId = Number(
    parseHeaderValue(headers, "vm-community-id", true)!,
  );
  const primaryCategoryNodeId = parseHeaderValue(
    headers,
    "vm-resource-primary-category",
    true,
  )!;
  const primarySuperCatNodeId = parseHeaderValue(
    headers,
    "vm-resource-primary-super-cat",
  );
  const primaryPackageNodeId = parseHeaderValue(headers, "vm-resource-package");
  const primaryStreamNodeId = parseHeaderValue(headers, "vm-resource-stream");
  const hasCampaign =
    parseHeaderValue(headers, "vm-resource-has-campaign") === "true";
  const isPaywallEligible = parseIsPaywallEligibleHeader(headers);

  if (res) {
    for (const headerKey in headers) {
      if (headerKey.startsWith("vm-resource")) {
        const headerValue = parseHeaderValue(headers, headerKey);

        if (headerValue) {
          res.setHeader(headerKey, headerValue);
        }
      }
    }
  }

  return {
    communityId,
    primaryPackageNodeId,
    primaryStreamNodeId,
    primaryCategoryNodeId,
    primarySuperCatNodeId,
    hasCampaign,
    isPaywallEligible,
  };
};

export const parseResourceTypeHeader = (
  headers: IncomingHttpHeaders,
): GQLEnumResourceTypeEnum | undefined => {
  const resourceType = (parseHeaderValue(headers, "vm-resource-type") ||
    undefined) as GQLEnumResourceTypeEnum | undefined;

  return resourceType;
};

/**
 * Determines a duration header key to be used based on the name of the GQL
 * operation. Duration headers are used to store data around the execution
 * time of GQL queries for testing and performance evaluation purposes.
 */
export const getDurationHeaderKeyForOperation = (operationName: string) => {
  let modifiedOperationName = operationName;

  // There will only be one layout query per page, so we don't need to specify
  if (operationName.includes("Layout")) {
    return "vm-duration-layout-query";
  }
  /**
   * The below conditions simply remove text from the operation name that
   * is not useful in understanding what the query is to avoid unnecessarily
   * long duration header keys.
   *
   * E.g. LoopNavProviderContainerQuery -> LoopNavProviderQuery
   * E.g. ArticlePackageRecircContainerQuery -> PackageRecircQuery
   */
  if (operationName.includes("ProviderContainerQuery")) {
    modifiedOperationName = operationName.replace("ProviderContainer", "");
  }
  if (operationName.includes("ContainerQuery")) {
    modifiedOperationName = operationName.replace("Container", "");
  }
  if (operationName.includes("ArticleRecirc")) {
    modifiedOperationName = operationName.replace("Article", "");
  }

  const kebabCaseOperationName = modifiedOperationName
    .replace(/([a-z])([A-Z])/g, "$1-$2")
    .toLowerCase();

  return `vm-duration-${kebabCaseOperationName}`;
};
